import {NavBarIconButton} from '@forecast-it/design-system';
import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import {TRACKING_OBJECTS} from '../../../../tracking/amplitude/constants/TrackingObjects';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import ForecastTooltip, {TooltipColor} from '../../../shared/components/tooltips/ForecastTooltip';
import useEventListener from '../../../shared/hooks/useEventListener';
import GlobalSearchModal from './GlobalSearchModal';

export const SEARCH_SHORTCUT = '/';
const ButtonIndicator = styled.div`
	margin: 0 4px;
	border-radius: 2px;
	padding: 2px 6px;
	background-color: #e7e7f3;
`;

const tooltipContent = (
	<div style={{display: 'flex', alignItems: 'center'}}>
		Use <ButtonIndicator>{SEARCH_SHORTCUT}</ButtonIndicator> to search faster
	</div>
);

type GlobalSearchProps = {
	companyId: string | null;
	personId: number | null;
};

const GlobalSearchV2 = ({companyId, personId}: GlobalSearchProps): JSX.Element => {
	const [showSearch, setShowSearch] = useState(false);

	const trackAndShowSearch = () => {
		trackEvent(TRACKING_OBJECTS.GLOBAL_SEARCH_MODAL, 'Opened');
		setShowSearch(true);
	};

	const hideSearch = useCallback(() => {
		setShowSearch(false);
	}, []);

	const handleSearchKeyDown = useCallback(
		e => {
			if (e.key === SEARCH_SHORTCUT) {
				if (e.defaultPrevented) return;
				if (e.ctrlKey || e.altKey || e.metaKey) return;
				if (/^(?:input|textarea|select)$/i.test(e.target.tagName)) return;
				if (
					document?.activeElement?.className.includes('DraftEditor') ||
					document?.activeElement?.className.includes('ck-editor')
				) {
					return;
				}
				if (document?.activeElement?.getAttribute('role') === 'textbox') {
					return;
				}
				e.stopPropagation();
				e.preventDefault();
				trackAndShowSearch();
			}
		},
		[showSearch]
	);

	useEventListener('keydown', handleSearchKeyDown);

	return (
		<>
			<ForecastTooltip content={tooltipContent} color={TooltipColor.PURPLE}>
				<NavBarIconButton
					title={'Site-wide search'}
					aria-label="search"
					tabIndex={0}
					icon="search"
					onClick={trackAndShowSearch}
				/>
			</ForecastTooltip>
			{showSearch ? <GlobalSearchModal companyId={companyId} personId={personId} hideSearch={hideSearch} /> : null}
		</>
	);
};

export default GlobalSearchV2;
